import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Clock } from "lucide-react";

/**
 * Modal that emails the adjuster a 24h share link + claim packet.
 * Reuses share-link rate limits (10/24h per claim). Email body contains a
 * "Open the claim packet (PDF)" CTA that opens the public /share/timeline
 * endpoint for 24 hours.
 */
export default function EmailPacketDialog({ open, onOpenChange, claim }) {
  const [toEmail, setToEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setToEmail("");
    setNote("");
  };

  const onSubmit = async () => {
    if (!toEmail || !/^\S+@\S+\.\S+$/.test(toEmail)) {
      return toast.error("Enter a valid email");
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/claims/${claim.id}/email-packet`, {
        to_email: toEmail,
        note: note || null,
      });
      toast.success(
        `Packet emailed to ${r.data.to} · link expires ${r.data.expires_at.slice(0, 10)}`,
      );
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="email-packet-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#C25934]" />
            Email claim packet to adjuster
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-600 leading-relaxed">
            We'll issue a 24-hour share link and email it directly. The recipient
            can open the PDF packet (photos, documents, timeline, signed Proof of
            Loss) without logging in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label htmlFor="to-email" className="text-xs text-gray-600">
              Adjuster email
            </Label>
            <Input
              id="to-email"
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="adjuster@carrier.com"
              autoComplete="email"
              data-testid="email-packet-to"
            />
          </div>
          <div>
            <Label htmlFor="note" className="text-xs text-gray-600">
              Personal note (optional)
            </Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Hi Alice — here's the full packet for my 2026-02-01 water-loss claim…"
              data-testid="email-packet-note"
            />
          </div>
          <p className="text-xs text-gray-500 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Share link valid for 24 hours. You can revoke it any time from the
            Share &amp; Analytics tab.
          </p>
        </div>

        <DialogFooter className="mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="email-packet-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="bg-[#C25934] hover:bg-[#a94a2b] text-white disabled:opacity-50"
            data-testid="email-packet-submit"
          >
            <Mail className="w-4 h-4" />
            {submitting ? "Sending…" : "Send packet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
